import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import {
  goBack,
  navigate,
  fetchNextTask,
  resetSkipSession,
  showToast,
  friendlyError,
  calendarStatus,
  loadCalendarStatus,
} from '../store.js';
import { __ } from '../i18n.js';
import { TIME_PRESETS } from '../lib/labels.js';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

export default function TimeScreen({ params }) {
  const [freeMinutes, setFreeMinutes] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedMinutes, setSuggestedMinutes] = useState(null);

  // Phase 5B "killer feature" (roadmap §7): prefill the time budget from
  // how long until the user's next calendar appointment. Only asks when at
  // least one provider is actually connected — GET /calendar/free-busy
  // degrades to {available_minutes: null} anyway, but skipping the call
  // entirely avoids a pointless request on every visit for the common case
  // (no calendar connected yet).
  //
  // calendarStatus is normally already populated by bootstrap()/
  // onAuthenticated() (both fire-and-forget it before the user can reach
  // this screen), but this screen doesn't rely on that timing — it
  // defensively loads it itself first if it's still null, same guard
  // SettingsScreen uses, rather than silently treating "not loaded yet" as
  // "no calendar connected" and permanently skipping the suggestion for
  // this visit.
  useEffect(() => {
    let cancelled = false;

    async function loadSuggestion() {
      if (calendarStatus.value === null) {
        await loadCalendarStatus();
      }
      if (cancelled) return;

      const providers = calendarStatus.value;
      const hasConnection = providers && Object.values(providers).some((p) => p.connected);
      if (!hasConnection) return;

      try {
        const res = await api.calendar.freeBusy();
        if (!cancelled && res.available_minutes) {
          setSuggestedMinutes(res.available_minutes);
        }
      } catch {
        // Purely additive — a failed lookup just means no suggestion chip,
        // never an error toast on a screen the user still needs to use.
      }
    }

    loadSuggestion();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSelect(minutes) {
    if (loading) return;
    const value = Number(minutes);
    if (!Number.isFinite(value) || value <= 0) {
      showToast(__('Vul een geldig aantal minuten in', 'etm'), 'error');
      return;
    }
    setLoading(true);
    try {
      await fetchNextTask(params.categoryId, value);
      // A fresh time-budget pick is a new attempt — the 2-skip friction
      // counter (roadmap §6) shouldn't carry over from a previous category
      // or time choice.
      resetSkipSession();
      navigate('task', { categoryId: params.categoryId, availableMinutes: value });
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <BackButton onClick={goBack} />
      <h1 class="etm-screen-title">{__('Hoeveel tijd?', 'etm')}</h1>
      <p class="etm-screen-subtitle">{__('Kies hoeveel tijd je hebt voor', 'etm') + ' ' + params.categoryName}</p>

      {suggestedMinutes ? (
        <button
          type="button"
          class="etm-suggestion-chip"
          disabled={loading}
          onClick={() => handleSelect(suggestedMinutes)}
        >
          <Icon name="lightbulb" size={20} />
          {__('Voorstel: %d min tot je volgende afspraak', 'etm').replace('%d', suggestedMinutes)}
        </button>
      ) : null}

      <div class="etm-selection-grid">
        {TIME_PRESETS.map((preset) => (
          <button
            key={preset.minutes}
            type="button"
            class="etm-selection-card"
            disabled={loading}
            onClick={() => handleSelect(preset.minutes)}
          >
            <span class="etm-category-icon">
              <Icon name={preset.icon} size={26} />
            </span>
            <span class="label">{preset.label()}</span>
            <span class="sublabel">{preset.sublabel()}</span>
          </button>
        ))}
      </div>

      <div class="etm-form-group">
        <label class="etm-form-label" for="etm-free-minutes">
          {__('Of vul zelf minuten in', 'etm')}
        </label>
        <div class="etm-form-row">
          <input
            id="etm-free-minutes"
            type="number"
            min="1"
            class="etm-form-input"
            value={freeMinutes}
            disabled={loading}
            onInput={(e) => setFreeMinutes(e.currentTarget.value)}
          />
          <button
            type="button"
            class="etm-btn etm-btn-secondary"
            disabled={loading}
            onClick={() => handleSelect(freeMinutes)}
          >
            {__('Kies', 'etm')}
          </button>
        </div>
      </div>
    </div>
  );
}
